import React, {useMemo, useState} from "react";
import {Alert, Button, Checkbox, Drawer, Form, Input, Modal, Radio, Select, Switch, Table, Tabs, Tag, Tooltip, message} from "antd";
import {ApiRolesService} from "../../../../../shared/api";
import {SectionToolbar, filterItems} from "../../components/SectionToolbar/SectionToolbar";

const errorOf = (error) => error?.response?.data?.errors || {}

const LEVEL_OPTIONS = [
  {value: 'none', label: 'Нет'},
  {value: 'view', label: 'Просмотр'},
  {value: 'edit', label: 'Правка'},
]

const SHOW = [
  {value: 'visible', label: 'Видит'},
  {value: 'hidden', label: 'Не видит'},
  {value: 'editable', label: 'Меняет'},
  {value: 'personal', label: 'Личные данные'},
]

const FLAT_RULE_KEYS = ['hidden', 'booked', 'staffOnly', 'individualTerms', 'internal', 'acMark', 'stock']

// «Какие квартиры видит»: categories (null = all) and the switches; a role saved before them had one switch
// «Скрытые квартиры» (hiddenFlats) for all of them but «Запас»
const flatsOf = (permissions = {}) => {
  const flats = permissions.flats
  if (!flats) {
    const on = permissions.hiddenFlats === true
    return {categories: null, ...Object.fromEntries(FLAT_RULE_KEYS.map((key) => [key, on && key !== 'stock']))}
  }
  return {categories: Array.isArray(flats.categories) ? new Set(flats.categories) : null, ...Object.fromEntries(FLAT_RULE_KEYS.map((key) => [key, flats[key] === true]))}
}

// the role's rights as the form keeps them: sections with 'none', field, category and filter lists as sets (null = all)
const toState = (permissions = {}) => ({
  sections: {...(permissions.sections || {})},
  portal: Boolean(permissions.portal),
  allClients: Boolean(permissions.allClients),
  flats: flatsOf(permissions),
  staffFilters: Array.isArray(permissions.staffFilters) ? new Set(permissions.staffFilters) : null,
  visible: permissions.fields && permissions.fields.visible === null ? null : new Set((permissions.fields && permissions.fields.visible) || []),
  editable: permissions.fields && permissions.fields.editable === null ? null : new Set((permissions.fields && permissions.fields.editable) || []),
})

const toPermissions = (state) => ({
  sections: Object.fromEntries(Object.entries(state.sections).filter(([, level]) => level && level !== 'none')),
  portal: state.portal,
  allClients: state.allClients,
  flats: {...state.flats, categories: state.flats.categories === null ? null : [...state.flats.categories]},
  staffFilters: state.staffFilters === null ? null : [...state.staffFilters],
  fields: {
    visible: state.visible === null ? null : [...state.visible],
    editable: state.editable === null ? null : [...state.editable],
  },
})

// Create or change a role: name, description, then its rights on three tabs. readOnly: the admin role, or a user
// with «Роли: просмотр».
export const RoleEditor = ({role, roles, catalog, readOnly, onClose, onSaved}) => {
  const [form] = Form.useForm()
  const [state, setState] = useState(() => toState(role ? role.permissions : {}))
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [show, setShow] = useState(undefined)
  const locked = Boolean(role && role.locked)
  const disabled = readOnly || locked

  const flats = state.sections.apartments && state.sections.apartments !== 'none'
  const flatsEdit = state.sections.apartments === 'edit'
  const sees = (field) => state.visible === null || state.visible.has(field)
  const changes = (field) => sees(field) && (state.editable === null || state.editable.has(field))

  const setSection = (key, level) => setState((s) => {
    const next = {...s, sections: {...s.sections, [key]: level}}
    // without «Квартиры» a role cannot have every field (they include the buyer's passport) nor the buyer's data
    if (key === 'apartments' && level === 'none') {
      const visible = s.visible === null
        ? new Set(catalog.fields.filter((f) => !f.locked).map((f) => f.field))
        : new Set([...s.visible].filter((field) => !catalog.fields.find((f) => f.field === field && f.locked)))
      next.visible = visible
      next.editable = s.editable === null ? new Set(visible) : new Set([...s.editable].filter((field) => visible.has(field)))
    }
    return next
  })

  const setFlag = (key, value) => setState((s) => ({...s, [key]: value}))
  const setFlats = (changes) => setState((s) => ({...s, flats: {...s.flats, ...changes}}))
  const tickCategory = (name, on) => setState((s) => {
    const categories = new Set(s.flats.categories || [])
    if (on) categories.add(name)
    else categories.delete(name)
    return {...s, flats: {...s.flats, categories}}
  })
  const tickFilter = (key, on) => setState((s) => {
    const list = new Set(s.staffFilters || [])
    if (on) list.add(key)
    else list.delete(key)
    return {...s, staffFilters: list}
  })
  // staff filters are for roles with a section of the panel (they make the user staff)
  const staffRole = locked || Object.values(state.sections).some((level) => level && level !== 'none')
  const ruleTitles = Object.fromEntries((catalog.flatRules || []).map((r) => [r.key, r.title]))
  // the categories the flats have, and the role's own ones no flat has any more (so they can be unticked)
  const categoryOptions = useMemo(() => {
    const known = catalog.categories || []
    const own = state.flats.categories ? [...state.flats.categories].filter((name) => !known.some((c) => c.name === name)) : []
    return [...known, ...own.map((name) => ({name, onSale: 0}))]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, role])

  const allFields = useMemo(() => new Set(catalog.fields.map((f) => f.field)), [catalog])

  const setSees = (fields, on) => setState((s) => {
    const visible = new Set(s.visible === null ? allFields : s.visible)
    const editable = s.editable === null ? new Set(visible) : new Set(s.editable)
    fields.forEach((field) => {
      if (on) visible.add(field)
      else {
        visible.delete(field)
        editable.delete(field)
      }
    })
    return {...s, visible, editable}
  })

  const setChanges = (fields, on) => setState((s) => {
    const visible = s.visible === null ? null : new Set(s.visible)
    const editable = new Set(s.editable === null ? (s.visible === null ? allFields : s.visible) : s.editable)
    fields.forEach((field) => {
      if (on) {
        editable.add(field)
        if (visible) visible.add(field)
      } else editable.delete(field)
    })
    return {...s, visible, editable}
  })

  // the field lists of another role, as a start
  const copyFields = (id) => {
    const source = (roles || []).find((r) => r.id === id)
    if (!source) return
    const from = toState(source.permissions)
    const visible = from.visible === null && !flats
      ? new Set(catalog.fields.filter((f) => !f.locked).map((f) => f.field))
      : from.visible
    setState((s) => ({...s, visible, editable: from.editable}))
    message.info(`Поля взяты из роли «${source.title || source.name}»`)
  }

  // opening personal data asks first
  const openFields = (items) => {
    const personal = items.filter((f) => f.personal && !sees(f.field))
    const apply = () => setSees(items.map((f) => f.field), true)
    if (!personal.length) return apply()
    Modal.confirm({
      title: 'Открыть личные данные?',
      content: `${personal.map((f) => `«${f.title}»`).join(', ')}: здесь данные людей. Их увидят все пользователи с этой ролью — в таблицах, карточках и выгрузках.`,
      okText: 'Открыть',
      okButtonProps: {danger: true},
      cancelText: 'Отмена',
      onOk: apply,
    })
  }

  const tickChanges = (f, on) => {
    if (on && f.personal && !sees(f.field)) {
      Modal.confirm({
        title: 'Открыть личные данные?',
        content: `«${f.title}»: здесь данные людей. Их увидят все пользователи с этой ролью.`,
        okText: 'Открыть',
        okButtonProps: {danger: true},
        cancelText: 'Отмена',
        onOk: () => setChanges([f.field], true),
      })
      return
    }
    setChanges([f.field], on)
  }

  const fields = useMemo(() => filterItems(catalog.fields, search, ['title', 'field']).filter((f) => {
    if (show === 'visible') return sees(f.field)
    if (show === 'hidden') return !sees(f.field)
    if (show === 'editable') return changes(f.field)
    if (show === 'personal') return f.personal || f.locked
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [catalog, search, show, state])

  const canTick = (f) => !disabled && (!f.locked || flats)

  const submit = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (e) {
      return
    }
    setSaving(true)
    try {
      const body = {title: values.title, description: values.description || null, permissions: toPermissions(state)}
      if (role) await ApiRolesService.update(role.id, body)
      else await ApiRolesService.create(body)
      message.success('Роль сохранена')
      onSaved()
    } catch (error) {
      const {message: text, field} = errorOf(error)
      if (field === 'title' || field === 'description') form.setFields([{name: field, errors: [text]}])
      else message.error(text || 'Не удалось сохранить')
    }
    setSaving(false)
  }

  const seenCount = state.visible === null ? catalog.fields.length : state.visible.size
  const changedCount = state.editable === null ? seenCount : [...state.editable].filter(sees).length

  const sectionColumns = [
    {
      title: 'Раздел',
      key: 'title',
      render: (s) => (
        <>
          <b>{s.title}</b>
          {s.hint && <div className='cell-hint'>{s.hint}</div>}
        </>
      ),
    },
    {
      title: 'Доступ',
      key: 'level',
      width: 300,
      render: (s) => (
        <Radio.Group
          optionType='button'
          size='small'
          disabled={disabled}
          options={LEVEL_OPTIONS}
          value={locked ? 'edit' : state.sections[s.key] || 'none'}
          onChange={(e) => setSection(s.key, e.target.value)}
        />
      ),
    },
  ]

  const fieldColumns = [
    {
      title: 'Поле',
      key: 'title',
      render: (f) => (
        <>
          <div style={{fontWeight: 600}}>{f.title}</div>
          <div className='cell-hint'>{f.field}</div>
        </>
      ),
    },
    {
      title: '',
      key: 'marks',
      width: 160,
      render: (f) => (
        <div className='tag-list'>
          {f.locked && <Tag color='red'>данные покупателя</Tag>}
          {!f.locked && f.personal && <Tag color='orange'>личные данные</Tag>}
        </div>
      ),
    },
    {
      title: 'Видит',
      key: 'sees',
      width: 90,
      align: 'center',
      render: (f) => {
        const box = (
          <Checkbox
            checked={sees(f.field)}
            disabled={!canTick(f) || state.visible === null}
            onChange={(e) => (e.target.checked ? openFields([f]) : setSees([f.field], false))}
          />
        )
        return f.locked && !flats
          ? <Tooltip title='Паспорт, ИНН и контакты покупателя — только для ролей с доступом к разделу «Квартиры»'>{box}</Tooltip>
          : box
      },
    },
    {
      title: 'Меняет',
      key: 'changes',
      width: 90,
      align: 'center',
      render: (f) => (
        <Checkbox
          checked={changes(f.field)}
          disabled={!canTick(f) || state.editable === null}
          onChange={(e) => tickChanges(f, e.target.checked)}
        />
      ),
    },
  ]

  const bulk = (on, column) => {
    const items = fields.filter((f) => !f.locked || flats)
    if (column === 'sees') return on ? openFields(items) : setSees(items.map((f) => f.field), false)
    return setChanges(items.map((f) => f.field), on)
  }

  return (
    <Drawer
      visible
      width={920}
      title={role ? (locked ? role.title : `Роль «${role.title}»`) : 'Новая роль'}
      onClose={onClose}
      destroyOnClose
      footer={!disabled && (
        <div style={{textAlign: 'right'}}>
          <Button onClick={onClose} style={{marginRight: 8}}>Отмена</Button>
          <Button type='primary' loading={saving} onClick={submit}>Сохранить</Button>
        </div>
      )}
    >
      {locked && <Alert className='section-note' type='info' showIcon message='У администратора всегда все права: роль не меняется и не удаляется.'/>}
      {!locked && readOnly && <Alert className='section-note' type='info' showIcon message='У вас «Роли: просмотр» — роль можно посмотреть, но не изменить.'/>}
      <Form form={form} layout='vertical' initialValues={{title: role ? role.title : '', description: role ? role.description || '' : ''}}>
        <Form.Item name='title' label='Название' rules={[{required: true, whitespace: true, message: 'Укажите название'}]}>
          <Input maxLength={60} disabled={disabled}/>
        </Form.Item>
        <Form.Item name='description' label='Описание' extra='Для кого роль и что она даёт — видно в списке ролей'>
          <Input.TextArea maxLength={500} autoSize={{minRows: 2, maxRows: 4}} disabled={disabled}/>
        </Form.Item>
      </Form>

      <Tabs defaultActiveKey='sections'>
        <Tabs.TabPane tab='Разделы админки' key='sections'>
          <Alert
            className='section-note'
            type='info'
            showIcon
            message='«Просмотр» — раздел виден в меню, менять в нём ничего нельзя. «Правка» — можно создавать, менять и удалять. Удалять квартиры и ЖК может только администратор.'
          />
          <Table rowKey='key' size='small' columns={sectionColumns} dataSource={catalog.sections} pagination={false}/>
        </Tabs.TabPane>

        <Tabs.TabPane tab='Портал агента' key='portal'>
          <div className='role-flags'>
            {catalog.flags.map((flag) => (
              <label key={flag.key} className='role-flags__item'>
                <Switch checked={locked || state[flag.key]} disabled={disabled} onChange={(on) => setFlag(flag.key, on)}/>
                <span>
                  <b>{flag.title}</b>
                  <span className='cell-hint'>{flag.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane tab='Квартиры на портале' key='flats'>
          <Alert
            className='section-note'
            type='info'
            showIcon
            message='Какие квартиры роль видит везде: в поиске, карточке, ЖК, шахматке, подборках и аналогах. Без галочек — только свободные квартиры отмеченных категорий. Если у пользователя несколько ролей, видно всё, что видит хотя бы одна.'
          />
          <div className='section-subtitle'>Категории (реклама)</div>
          <div className='cell-hint' style={{marginBottom: 8}}>В скобках — сколько квартир категории сейчас в продаже.</div>
          <div className='role-fields-all'>
            <label>
              <Switch
                checked={locked || state.flats.categories === null}
                disabled={disabled}
                onChange={(on) => setFlats({categories: on ? null : new Set()})}
              />
              <span>Все категории, включая новые</span>
            </label>
            {!disabled && state.flats.categories !== null && (
              <Button
                size='small'
                onClick={() => setFlats({categories: new Set([...state.flats.categories, ...categoryOptions.filter((c) => /^1/.test(c.name)).map((c) => c.name)])})}
              >
                Отметить все «1…» (наши эксклюзивы)
              </Button>
            )}
          </div>
          {!locked && state.flats.categories !== null && (
            <div className='role-categories'>
              {categoryOptions.map((c) => (
                <Checkbox
                  key={c.name}
                  checked={state.flats.categories.has(c.name)}
                  disabled={disabled}
                  onChange={(e) => tickCategory(c.name, e.target.checked)}
                >
                  {c.name}
                  <span className='cell-hint' title='Квартир в продаже'> ({c.onSale})</span>
                </Checkbox>
              ))}
            </div>
          )}
          <div className='section-subtitle'>Ещё видит</div>
          <div className='role-flags'>
            {(catalog.flatRules || []).map((rule) => (
              <label key={rule.key} className='role-flags__item'>
                <Checkbox checked={locked || state.flats[rule.key]} disabled={disabled} onChange={(e) => setFlats({[rule.key]: e.target.checked})}/>
                <span>
                  <b>{rule.title}</b>
                  <span className='cell-hint'>{rule.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <div className='section-subtitle'>Служебные фильтры поиска</div>
          {!staffRole && <div className='cell-hint'>Служебные фильтры есть только у ролей с разделами админки (вкладка «Разделы админки»).</div>}
          {staffRole && (
            <>
              <div className='role-fields-all'>
                <label>
                  <Switch
                    checked={locked || state.staffFilters === null}
                    disabled={disabled}
                    onChange={(on) => setState((s) => ({...s, staffFilters: on ? null : new Set((catalog.staffFilters || []).map((f) => f.key))}))}
                  />
                  <span>Все служебные фильтры, включая новые</span>
                </label>
                <span className='cell-hint'>Фильтр по квартирам, которых роль не видит (Запас, Доступ руководств., ...), или по полю, которого не видит, ей не покажется.</span>
              </div>
              {!locked && state.staffFilters !== null && (
                <div className='role-categories'>
                  {(catalog.staffFilters || []).map((f) => (
                    <Checkbox
                      key={f.key}
                      checked={state.staffFilters.has(f.key)}
                      disabled={disabled}
                      onChange={(e) => tickFilter(f.key, e.target.checked)}
                    >
                      {f.title}
                      {f.requires && !state.flats[f.requires] && <span className='cell-hint'> · нужна галочка «{ruleTitles[f.requires]}»</span>}
                    </Checkbox>
                  ))}
                </div>
              )}
            </>
          )}
        </Tabs.TabPane>

        <Tabs.TabPane tab={`Поля квартир · ${seenCount}`} key='fields'>
          <Alert
            className='section-note'
            type='info'
            showIcon
            message='«Видит» — поле приходит пользователю везде: на портале агента (таблица, карточка, выгрузки) и в админке. «Меняет» — поле можно править в разделе «Квартиры» (нужна «Квартиры: правка»).'
          />
          <div className='role-fields-all'>
            <label>
              <Switch
                checked={locked || state.visible === null}
                disabled={disabled || !flats}
                onChange={(on) => setState((s) => ({...s, visible: on ? null : new Set(allFields)}))}
              />
              <span>Видит все поля, включая новые{!flats && <span className='cell-hint'> — только с доступом к разделу «Квартиры»</span>}</span>
            </label>
            <label>
              <Switch
                checked={locked || state.editable === null}
                disabled={disabled}
                onChange={(on) => setState((s) => ({...s, editable: on ? null : new Set(s.visible === null ? allFields : s.visible)}))}
              />
              <span>Меняет всё, что видит{!flatsEdit && <span className='cell-hint'> — действует при «Квартиры: правка»</span>}</span>
            </label>
            <span className='cell-hint'>Видит {seenCount} из {catalog.fields.length}, меняет {changedCount}</span>
            {!disabled && (
              <Select
                size='small'
                style={{width: 230}}
                placeholder='Скопировать поля из роли…'
                value={null}
                onChange={copyFields}
                options={(roles || []).filter((r) => !role || r.id !== role.id).map((r) => ({value: r.id, label: r.title || r.name}))}
              />
            )}
          </div>
          <Table
            rowKey='field'
            size='small'
            title={() => (
              <SectionToolbar
                search={search}
                onSearch={setSearch}
                placeholder='Название поля'
                filters={<Select allowClear style={{width: 180}} placeholder='Все поля' value={show} onChange={setShow} options={SHOW}/>}
              >
                {!disabled && (
                  <>
                    <Button size='small' disabled={state.visible === null} onClick={() => bulk(true, 'sees')}>Видит все найденные</Button>
                    <Button size='small' disabled={state.visible === null} onClick={() => bulk(false, 'sees')}>Не видит</Button>
                    <Button size='small' disabled={state.editable === null} onClick={() => bulk(true, 'changes')}>Меняет все найденные</Button>
                    <Button size='small' disabled={state.editable === null} onClick={() => bulk(false, 'changes')}>Не меняет</Button>
                  </>
                )}
              </SectionToolbar>
            )}
            columns={fieldColumns}
            dataSource={fields}
            pagination={{pageSize: 50, showSizeChanger: false, showTotal: (total) => `${total} полей`}}
            locale={{emptyText: 'Ничего не нашлось'}}
          />
        </Tabs.TabPane>
      </Tabs>
    </Drawer>
  )
}
